import { ConfigurationDataProps, FOUNDATION_ENDPOINTS, FOUNDATION_ROUTES, StipulatorConfigurationService } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext, { Contract, TwsFunction } from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import { ThemeColor, ThemeTonalRange } from '@philips-emr/tws-constants';
import { ConfigurationService, HttpService, LocaleService, MessageService, ThemeService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { applicationContext, foundationContext, Information } from '@philips-emr/tws-foundation/lib/context';

import registerAtendimento from './registries/atendimento-register';
import registerConsulta from './registries/consulta-register';
import registerDinamico from './registries/dinamico-register';
import registerFinanceiro from './registries/financeiro-register';
import registerGestaoContrato from './registries/gestao-contrato-register';
import registerInformacaoContratual from './registries/informacao-contratual-register';
import registerInicio from './registries/inicio-register';
import registerRelatorios from './registries/relatorios-register';

interface Theme {
  dsColorRange: ThemeColor;
  dsContTonalRange: ThemeTonalRange;
  dsLoginTonalRange: ThemeTonalRange;
}

interface InternalInformation extends Information {
  contracts: Contract[],
  allowedTwsFunctions: TwsFunction[],
}

function map(obj: object): Information {
  const response = obj as any;

  const information = response.body as InternalInformation;

  StipulatorContext.init(information.contracts, information.allowedTwsFunctions);

  return information;
}

function bootstrap(): Promise<void> {
  ConfigurationService.setAuthenticationConfiguration({
    clientId: 'stipulator',
    endpointMappers: {
      // @ts-ignore
      information: map,
    },
    endpoints: {
      information: {
        url: FOUNDATION_ENDPOINTS.INFORMATION,
        method: HttpMethod.GET,
      },
      refresh: {
        url: FOUNDATION_ENDPOINTS.REFRESH,
        method: HttpMethod.POST,
      },
      revoke: {
        url: FOUNDATION_ENDPOINTS.REVOKE,
        method: HttpMethod.POST,
      },
    },
    inactivity: {
      time: 15 * 60 * 1000,
    },
    routes: {
      custom: [
        FOUNDATION_ROUTES.NEW_PASSWORD,
        FOUNDATION_ROUTES.CONFIRMAR_USUARIO,
        FOUNDATION_ROUTES.CONFIRMAR_USUARIO_FALHA,
        FOUNDATION_ROUTES.CONFIRMAR_USUARIO_NOVA_SENHA,
        FOUNDATION_ROUTES.RENOVAR_SENHA_BY_USERNAME,

      ],
      forgotPassword: FOUNDATION_ROUTES.FORGOT_PASSWORD,
      home: FOUNDATION_ROUTES.HOME,
      inactivity: FOUNDATION_ROUTES.INACTIVITY,
      login: FOUNDATION_ROUTES.LOGIN,
    },
  });

  applicationContext.setTitle(MessageService.msg('stipulator.application.name'));
  foundationContext.setModuleSearchExpression('tws.finder.search');
  foundationContext.setHideModuleSearch(false);
  foundationContext.setCanEditEstablishment(false);
  foundationContext.setCanEditProfile(false);
  foundationContext.setCanEditSector(false);

  registerInicio();
  registerInformacaoContratual();
  registerGestaoContrato();
  registerFinanceiro();
  registerConsulta();
  registerAtendimento();
  registerRelatorios();
  registerDinamico();

  const themePromise = HttpService.get(FOUNDATION_ENDPOINTS.THEME)
    .then(response => {
      let { dsColorRange, dsContTonalRange, dsLoginTonalRange } = response.body as Theme;

      dsColorRange = dsColorRange || ThemeColor.BLUE;
      dsContTonalRange = dsContTonalRange || ThemeTonalRange.ULTRA_LIGHT;
      dsLoginTonalRange = dsLoginTonalRange || ThemeTonalRange.BRIGHT;

      ThemeService.setColors(dsColorRange, ThemeService.getSupportedAccents(dsColorRange)[0] as any);
      ThemeService.setContentTonalRange(dsContTonalRange);
      ThemeService.setNavigationTonalRange(ThemeTonalRange.BRIGHT);
      StipulatorConfigurationService.setTonalidadeLogin(dsLoginTonalRange);

      return response.body as ConfigurationDataProps;
    });

  const language = LocaleService.init({ defaultLocale: 'pt-BR', supportedLocales: ['pt-BR'] });

  StipulatorConfigurationService.setFileuploaderConfig();
  StipulatorConfigurationService.setConfigurationData(themePromise);

  return Promise.all([themePromise, language]).then(() => Promise.resolve());
}

export {
  bootstrap,
};
